/**
 * Drop-target resolution for drags — centralized and pure.
 *
 * ONE function (computeDropTarget) decides the single {targetId, zone} pair
 * for the current cursor position, using a single global rule:
 *
 *   Every eligible block contributes four candidate insertion edges —
 *   top/bottom (insert as sibling in that block's list, which nests when the
 *   block lives inside a callout/toggle) and left/right (column split, like
 *   Notion). The cursor snaps to the NEAREST edge segment overall.
 *
 * That one metric covers everything the old special-cases fought over:
 *  - inside a callout, the children's edges are closest → reorder/nest inside
 *  - near the callout's own border/outside it, the shell's edges win → drop
 *    beside/above/below the callout (including callout-next-to-callout)
 *  - dragging a callout over another callout's body reaches the children's
 *    edges → callouts can nest inside callouts
 *
 * Only row/column layout wrappers and the dragged block's *descendants* are
 * excluded. The dragged block itself still competes as a candidate so you can
 * drop it back on its original footprint (handleBlockDrop no-ops on self).
 * Blocks hidden from layout (e.g. inside a closed toggle) have zero-size
 * rects and are skipped.
 */

// On a near-tie, prefer vertical placement (matches the old bottom/right bias)
const ZONE_PRIORITY = { bottom: 0, top: 1, right: 2, left: 3 };
const TIE_EPSILON = 0.5;

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

/** Distance from the cursor to each of a rect's four edge segments. */
export const edgeDistances = (rect, x, y) => {
  const cx = clamp(x, rect.left, rect.right);
  const cy = clamp(y, rect.top, rect.bottom);
  return [
    { zone: 'top', dist: Math.hypot(x - cx, y - rect.top) },
    { zone: 'bottom', dist: Math.hypot(x - cx, y - rect.bottom) },
    { zone: 'left', dist: Math.hypot(x - rect.left, y - cy) },
    { zone: 'right', dist: Math.hypot(x - rect.right, y - cy) },
  ];
};

// Which edge of this block is the cursor closest to? (kept for callers/tests
// that reason about a single rect)
export const calculateStandardDropZone = ({ rect, clientX, clientY }) => {
  let best = null;
  for (const cand of edgeDistances(rect, clientX, clientY)) {
    if (
      !best ||
      cand.dist < best.dist - TIE_EPSILON ||
      (Math.abs(cand.dist - best.dist) <= TIE_EPSILON &&
        ZONE_PRIORITY[cand.zone] < ZONE_PRIORITY[best.zone])
    ) {
      best = cand;
    }
  }
  return best.zone;
};

const isBetterCandidate = (cand, best) =>
  !best ||
  cand.dist < best.dist - TIE_EPSILON ||
  (Math.abs(cand.dist - best.dist) <= TIE_EPSILON &&
    ZONE_PRIORITY[cand.zone] < ZONE_PRIORITY[best.zone]);

/**
 * The single source of truth: where would the dragged block land right now?
 *
 *  1. Over the bottom drop space (or well below everything) → after the last
 *     top-level block.
 *  2. Otherwise → globally nearest insertion edge (see file header).
 *
 * `draggedId` (optional): the block being dragged. Its original footprint is
 * a valid "stay put" target so the nearest-edge rule can snap back home.
 */
export const computeDropTarget = (x, y, allBlocks, excludedIds, draggedId = null) => {
  const afterLastTopLevel = () => {
    const lastTopLevel = [...allBlocks]
      .reverse()
      .find((b) => b.id === draggedId || !excludedIds.has(b.id));
    return lastTopLevel ? { targetId: lastTopLevel.id, zone: 'bottom' } : null;
  };

  const elements =
    typeof document.elementsFromPoint === 'function' ? document.elementsFromPoint(x, y) : [];
  if (elements.some((el) => el.classList?.contains('bottom-drop-space'))) {
    return afterLastTopLevel();
  }

  // One DOM pass: id → wrapper element for every rendered block
  const elementById = new Map();
  document.querySelectorAll('.block-wrapper[data-block-id]').forEach((el) => {
    elementById.set(el.getAttribute('data-block-id'), el);
  });

  let best = null; // { targetId, zone, dist }
  let lowestBlockBottom = -Infinity;

  const considerBlock = (blockId, zones = null) => {
    const element = elementById.get(blockId);
    if (!element) return;
    const rect = element.getBoundingClientRect();
    // Not laid out (closed toggle, hidden subtree) → not a target
    if (rect.width === 0 && rect.height === 0) return;

    if (rect.bottom > lowestBlockBottom) lowestBlockBottom = rect.bottom;

    for (const cand of edgeDistances(rect, x, y)) {
      if (zones && !zones.includes(cand.zone)) continue;
      const next = { targetId: blockId, zone: cand.zone, dist: cand.dist };
      if (isBetterCandidate(next, best)) best = next;
    }
  };

  // Whole original rect = home. Inside → distance 0 so neighbors can't steal it.
  const considerHomeBlock = (blockId) => {
    const element = elementById.get(blockId);
    if (!element) return;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;

    if (rect.bottom > lowestBlockBottom) lowestBlockBottom = rect.bottom;

    const inside =
      x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    const zone = calculateStandardDropZone({ rect, clientX: x, clientY: y });
    const dist = inside ? 0 : Math.min(...edgeDistances(rect, x, y).map((c) => c.dist));
    const next = { targetId: blockId, zone, dist };
    if (isBetterCandidate(next, best)) best = next;
  };

  const traverse = (blockList, inTableCell = false) => {
    blockList.forEach((block) => {
      if (excludedIds.has(block.id)) {
        // Drag root can still win (stay put); its descendants never can.
        if (block.id === draggedId) considerHomeBlock(block.id);
        return;
      }
      // Layout containers are never drop targets — only their contents.
      // Cells have no columns, so only top/bottom compete inside a cell.
      if (block.type !== 'row' && block.type !== 'column' && block.type !== 'choice-path') {
        considerBlock(block.id, inTableCell ? ['top', 'bottom'] : null);
      }
      if (block.children?.length) traverse(block.children, inTableCell);
      if (block.type === 'table' && block.props?.tableData) {
        const cells = [
          ...(block.props.tableData.headers || []),
          ...(block.props.tableData.rows || []).flat(),
        ];
        for (const cell of cells) {
          if (Array.isArray(cell)) traverse(cell, true);
        }
      }
    });
  };
  traverse(allBlocks);

  // Well below everything → land after the last TOP-LEVEL block (full width)
  if (y > lowestBlockBottom + 50) return afterLastTopLevel();

  return best ? { targetId: best.targetId, zone: best.zone } : null;
};
